import api from './api';

// ---------------------------------------------------------------------------
// 📢 کانال‌ها و گروه‌های تلگرامِ متصل به فروشگاه‌ها — /admin/telegram-channels
// ---------------------------------------------------------------------------

export type TgChannelStatus = 'active' | 'issue' | 'suspended' | 'disconnected';

export const TG_STATUS_LABELS: Record<TgChannelStatus, string> = {
  active: 'فعال',
  issue: 'مشکل ردیابی',
  suspended: 'معلق (مدیریت)',
  disconnected: 'قطع‌شده',
};

export const TG_STATUS_COLOR: Record<TgChannelStatus, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  issue: 'warning',
  suspended: 'error',
  disconnected: 'default',
};

export interface TgOverview {
  total: number;
  channels: number;
  groups: number;
  platform: number;
  active: number;
  suspended: number;
  disconnected: number;
  issues: number;
  autoPublishOff: number;
  storesConnected: number;
  totalActiveStores: number;
  totalMembers: number;
  activePosts: number;
  orders30d: number;
  revenue30d: number;
  errors24h: number;
  bridge: { ok: boolean; message: string; url?: string };
}

export interface TgChannelRow {
  id: string;
  chatId: number;
  title: string | null;
  username: string | null;
  publicUrl: string | null;
  privateUrl: string;
  chatType: 'channel' | 'group';
  purpose: 'sales' | 'community';
  ownerType: 'store' | 'platform';
  status: TgChannelStatus;
  active: boolean;
  autoPublish: boolean;
  canInvite: boolean;
  trackingIssue: string | null;
  suspended: { at: string; reason: string | null } | null;
  adminNote: string | null;
  memberCount: number | null;
  memberCountAt: string | null;
  referralEnabled: boolean;
  connectedAt: string | null;
  updatedAt: string | null;
  store: { id: string; name: string; slug: string; logo: string | null; status: string } | null;
  owner: {
    id: string;
    name: string;
    phone: string | null;
    telegram: { username: string | null; firstName: string | null } | null;
  } | null;
  stats: {
    activePosts: number;
    posts30d: number;
    lastPostAt: string | null;
    clicks: number;
    orders30d: number;
    revenue30d: number;
    errors24h: number;
  };
}

export interface TgChannelDetail extends Omit<TgChannelRow, 'stats' | 'owner' | 'referralEnabled'> {
  trackedMembers: number;
  discussionGroup: { chatId: number; title: string | null; username: string | null } | null;
  owner: (TgChannelRow['owner'] & { email: string | null }) | null;
  settings: {
    referralEnabled: boolean;
    referralQualifyDays: number;
    requireJoinApproval: boolean;
    filters: {
      categories: number;
      accountTypes: number;
      tags: string[];
      minPrice: number | null;
      maxPrice: number | null;
      onlyDiscounted: boolean;
      minStock: number | null;
      stores: number;
    };
    schedule: { quietFrom?: number; quietTo?: number; maxPerHour?: number };
    digest: { mode: 'off' | 'daily' | 'weekly'; hour?: number; weekday?: number };
    template: {
      showDescription: boolean;
      showSpecs: boolean;
      showVariants: boolean;
      showStock: boolean;
      hashtags: string[];
      signature: string;
      buyButton: 'site' | 'bot';
    };
    rules: {
      mode: 'all' | 'manual';
      manualProducts: number;
      soldOutPolicy: 'label' | 'delete' | 'keep';
      priceDropPosts: boolean;
      priceDropMinPercent: number;
      featuredProductId: string | null;
    };
    memberDiscount: boolean;
    moderationMode: 'off' | 'delete' | 'warn' | null;
  };
  summary: any;
  posts: Array<{
    id: string;
    title: string | null;
    status: string;
    kind: string;
    clicks: number;
    visits: number;
    reactions: number;
    createdAt: string;
    url: string | null;
  }>;
  activity: Array<{
    id: string;
    type: string;
    level: 'info' | 'warn' | 'error';
    message: string;
    productTitle: string | null;
    createdAt: string;
  }>;
}

export interface TgStoreWithoutChannel {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  createdAt: string;
  owner: { id: string; name: string; phone: string | null } | null;
  telegramLinked: boolean;
  telegramUsername: string | null;
  previousChannel: { title: string | null; username: string | null; suspended: boolean; at: string } | null;
}

export interface TgListQuery {
  q?: string;
  ownerType?: string;
  chatType?: string;
  purpose?: string;
  status?: string;
  sort?: string;
  page?: number;
  limit?: number;
}

/** api.get ممکن است پاکت { success, data } را باز کند یا نکند؛ هر دو حالت */
const unwrap = <T>(res: any): T => {
  if (res && typeof res === 'object' && 'success' in res && 'data' in res) return res.data as T;
  if (res && typeof res === 'object' && 'status' in res && 'data' in res) return unwrap<T>(res.data);
  return res as T;
};

/** پیام پاسخ (برای عملیاتی که متن توضیحی برمی‌گردانند) */
const messageOf = (res: any): string | undefined =>
  res?.message || res?.data?.message || undefined;

const BASE = '/admin/telegram-channels';
const enc = (chatId: number) => encodeURIComponent(String(chatId));

const telegramChannelsService = {
  async overview(): Promise<TgOverview> {
    return unwrap(await api.get<any>(`${BASE}/overview`));
  },
  async list(query: TgListQuery): Promise<{ items: TgChannelRow[]; total: number }> {
    const params: Record<string, unknown> = {};
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== null) params[k] = v;
    });
    return unwrap(await api.get<any>(BASE, { params } as any));
  },
  async storesWithoutChannel(query: { q?: string; page?: number; limit?: number }): Promise<{ items: TgStoreWithoutChannel[]; total: number }> {
    return unwrap(await api.get<any>(`${BASE}/stores-without-channel`, { params: query } as any));
  },
  async detail(chatId: number): Promise<TgChannelDetail> {
    return unwrap(await api.get<any>(`${BASE}/${enc(chatId)}`));
  },
  async suspend(chatId: number, reason: string): Promise<string | undefined> {
    return messageOf(await api.post<any>(`${BASE}/${enc(chatId)}/suspend`, { reason }));
  },
  async unsuspend(chatId: number): Promise<string | undefined> {
    return messageOf(await api.post<any>(`${BASE}/${enc(chatId)}/unsuspend`, {}));
  },
  async setAutoPublish(chatId: number, enabled: boolean): Promise<void> {
    await api.post<any>(`${BASE}/${enc(chatId)}/auto-publish`, { enabled });
  },
  async sync(chatId: number): Promise<string | undefined> {
    return messageOf(await api.post<any>(`${BASE}/${enc(chatId)}/sync`, {}));
  },
  async refresh(chatId: number): Promise<{ message?: string; botPresent?: boolean }> {
    const res: any = await api.post<any>(`${BASE}/${enc(chatId)}/refresh`, {});
    const data: any = unwrap(res);
    return { message: messageOf(res), botPresent: data?.botPresent };
  },
  async inviteLink(chatId: number): Promise<string> {
    const data: any = unwrap(await api.post<any>(`${BASE}/${enc(chatId)}/invite-link`, {}));
    return data?.inviteLink;
  },
  async saveNote(chatId: number, note: string): Promise<void> {
    await api.put<any>(`${BASE}/${enc(chatId)}/note`, { note });
  },
  async remove(chatId: number): Promise<void> {
    await api.delete<any>(`${BASE}/${enc(chatId)}`);
  },
};

export default telegramChannelsService;
